import { proxy } from 'valtio';
import type { FlowHistoryItem } from '../flowEditorState';
import { classifyFlowChange, type FlowAction } from './classifyFlowChange';

// -- Undo inspector debug store -------------------------------------------------
// A development instrument for the Flow Editor undo/redo system. The undo
// recorder is a single chokepoint that sees only "the graph changed"; this store
// makes WHAT it captured, skipped, or discarded directly visible, so a missing
// or wrong undo entry is obvious instead of inferred from behaviour.
//
// It is a SEPARATE valtio store from flowEditorState on purpose: the inspector
// panel reads it via useSnapshot, and the panel renders OUTSIDE the React Flow
// canvas. Pushing inspector events therefore never re-renders the canvas or a
// node, so it cannot perturb the keystroke-performance path it is meant to
// observe. When the inspector is off, the recorder skips pushing entirely.

export type InspectorReason =
  | 'recorded' // a genuine edit pushed the previous graph onto the past stack
  | 'gated-noop' // a clientVersion bump that did not change the conversation
  | 'text-run-coalesced' // a text edit folded into the open typing run (no new entry)
  | 'retracted-noop-run' // a typing run reverted to its start; its entry was popped
  | 'suppressed-restore' // an undo/redo's own bump, correctly ignored
  | 'undo'
  | 'redo';

export type InspectorStackEntry = {
  // What this entry would undo to, named in recognisable terms.
  action: FlowAction;
  nodeCount: number;
  edgeCount: number;
  // The message-bubble texts of the graph this entry restores to, one string per
  // node (bubbles joined by '·', nodes by ' | '). Without this every entry reads
  // the same opaque "delete message" and you cannot tell an entry that restores to
  // [edit1] from one that restores to []: the difference between a clean undo and a
  // stranded first message. This is the diagnostic that distinguishes them.
  restoresTo: string;
};

export type InspectorLogLine = {
  seq: number;
  reason: InspectorReason;
  // The action this event concerns (the diff that triggered it), when known.
  action: FlowAction | null;
  pastDepth: number;
  futureDepth: number;
  // The markDirty source label that triggered this recorder pass, so an uncovered
  // or mis-attributed bump (e.g. a restore write that should have been suppressed)
  // is visible in the log instead of anonymous. Dev diagnostic only.
  dirtySource: string;
};

const MAX_LOG = 200;

class UndoInspectorState {
  // Whether the panel is open. Off by default; summoned via window.undoInspector().
  // The recorder's push-guard reads this too, so logging is skipped when closed.
  enabled = false;

  // Debugging switch: when true the autosave debounce is skipped, so edits do not
  // persist on their own. Lets a tester drive the editor and inspect the recorder
  // without save-driven churn; Cmd+S still forces a save. Toggled from the panel.
  autosaveDisabled = false;

  // Last autosave's measured cost, surfaced in the panel so we can decide
  // whole-document vs delta saving from real data. The whole document is sent on
  // every save today; if these grow large on real flows that is the trigger to
  // move to incremental saves. null until the first save.
  lastSavePayloadBytes: number | null = null;
  lastSaveDurationMs: number | null = null;

  // Mirrors of the undo/redo stacks, summarised for display (newest last, like
  // the real stacks). Rebuilt whenever the recorder/undo/redo mutate history.
  past: InspectorStackEntry[] = [];
  future: InspectorStackEntry[] = [];

  // Rolling log of recorder decisions, newest last. Capped so it can't grow
  // without bound during a long session.
  log: InspectorLogLine[] = [];

  private seq = 0;

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    // Closing the panel re-enables autosave, so a paused save can't outlive it.
    if (!enabled) {
      this.autosaveDisabled = false;
    }
  }

  toggle() {
    this.setEnabled(!this.enabled);
  }

  clear() {
    this.past = [];
    this.future = [];
    this.log = [];
    this.seq = 0;
  }

  // Push one decision to the log and stamp the current stack depths onto it.
  // `dirtySource` names the markDirty that triggered this pass (the recorder reads
  // it off flowEditorState.lastDirtySource), so an uncovered bump is attributable.
  logEvent(reason: InspectorReason, action: FlowAction | null, dirtySource = '') {
    if (!this.enabled) {
      return;
    }
    this.seq += 1;
    this.log = [
      ...this.log.slice(Math.max(0, this.log.length - MAX_LOG + 1)),
      {
        seq: this.seq,
        reason,
        action,
        pastDepth: this.past.length,
        futureDepth: this.future.length,
        dirtySource,
      },
    ];
  }

  // Replace the displayed stacks. The recorder/undo/redo hand the raw history
  // stacks (arrays of graphs) plus the live current graph; we classify each
  // entry against the one that follows it (what undoing to it would change) so
  // the label reads as the action that entry reverses.
  setStacks(past: FlowHistoryItem[], future: FlowHistoryItem[], current: FlowHistoryItem) {
    if (!this.enabled) {
      return;
    }
    this.past = summariseStack([...past, current]);
    // For the future (redo) stack, each entry is classified against the entry
    // below it in redo order (current, then future top, ...), i.e. what redoing
    // INTO it would change.
    this.future = summariseStack([current, ...future])
      .slice(1)
      .reverse();
  }

  // A plain-text dump of the current stacks and log, for pasting into a bug
  // report. Newest-first to match the panel, with a clear divider between the
  // redo and undo stacks. Pure string building so it can be copied to the
  // clipboard from the panel and asserted in a test.
  formatReport(): string {
    const stackLine = (e: InspectorStackEntry) =>
      `  ${e.action.label}${e.action.summary ? ` — ${e.action.summary}` : ''}  (${e.nodeCount}n ${e.edgeCount}e) → [${e.restoresTo}]`;
    const logLine = (l: InspectorLogLine) =>
      `  #${l.seq} ${l.reason}${l.action ? ` — ${l.action.label} ${l.action.summary}` : ''}  [past:${l.pastDepth} future:${l.futureDepth}]${l.dirtySource ? ` «${l.dirtySource}»` : ''}`;

    const future = [...this.future].map(stackLine);
    const past = [...this.past].reverse().map(stackLine);
    const log = [...this.log].reverse().map(logLine);

    return [
      '=== UNDO INSPECTOR ===',
      `REDO (future) — ${this.future.length}`,
      ...(future.length ? future : ['  (empty)']),
      '--- YOU ARE HERE ---',
      `UNDO (past) — ${this.past.length}`,
      ...(past.length ? past : ['  (empty)']),
      '',
      'RECORDER LOG (newest first)',
      ...(log.length ? log : ['  (empty)']),
    ].join('\n');
  }
}

// Given a sequence of graphs ordered oldest -> newest, label each (except the
// last) by the change to the NEXT graph: that is the action moving off this
// state would apply, i.e. what undoing back to it reverses. Returned oldest
// first (matching the past stack's bottom -> top order).
function summariseStack(graphs: FlowHistoryItem[]): InspectorStackEntry[] {
  const entries: InspectorStackEntry[] = [];
  for (let i = 0; i < graphs.length - 1; i++) {
    const before = graphs[i];
    const after = graphs[i + 1];
    if (!before || !after) {
      continue;
    }
    const action = classifyFlowChange(before, after) ?? {
      kind: 'edit',
      label: 'edit',
      summary: '',
    };
    entries.push({
      action,
      nodeCount: before.nodes.length,
      edgeCount: before.edges.length,
      restoresTo: graphBubbleTexts(before),
    });
  }
  return entries;
}

// The message-bubble texts of every node in a graph, for showing what an undo
// entry restores to. Each node becomes its bubbles' text joined by '·' (an empty
// bubble shows as ''); nodes are joined by ' | '. A node with no message bubbles
// (options-only) contributes an empty string, so the count of segments still maps
// to nodes. Best-effort and shape-tolerant: this is a dev diagnostic, not a
// contract, so it reads text defensively rather than assuming the tiptap shape.
function graphBubbleTexts(graph: FlowHistoryItem): string {
  const nodeText = (tiptap: unknown): string => {
    const content =
      tiptap &&
      typeof tiptap === 'object' &&
      Array.isArray((tiptap as { content?: unknown }).content)
        ? ((tiptap as { content: unknown[] }).content as unknown[])
        : [];
    const bubbleText = (block: unknown): string => {
      let text = '';
      const walk = (node: unknown) => {
        if (node && typeof node === 'object') {
          const n = node as { text?: unknown; content?: unknown };
          if (typeof n.text === 'string') {
            text += n.text;
          }
          if (Array.isArray(n.content)) {
            n.content.forEach(walk);
          }
        }
      };
      walk(block);
      return text;
    };
    return content.map(bubbleText).join('·');
  };
  return graph.nodes.map((node) => nodeText(node.data?.tiptap)).join(' | ');
}

export const undoInspectorState = proxy(new UndoInspectorState());
